var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from 'react-query';
import { NotificationsClient, } from '@madbox/notifications-client';
import { useEffect } from 'react';
var pullInterval = 1000 * 60 * 5;
/**
 * get the latest notifications, refreshed every X minutes
 */
export default function useNotifications(options) {
    var query = useQuery(['user-notifications'], function () { return NotificationsClient.instance.getNotifications(); }, {
        enabled: options === null || options === void 0 ? void 0 : options.enabled,
        refetchOnWindowFocus: true,
        refetchInterval: pullInterval,
        cacheTime: pullInterval,
        staleTime: pullInterval,
    });
    return { isLoading: query.isLoading, error: query.error, data: query.data };
}
function markNotificationAsRead(notifications, _id, read) {
    return notifications.map(function (n) {
        if (n._id === _id) {
            return __assign(__assign({}, n), { read_at: read ? new Date().toISOString() : null });
        }
        return n;
    });
}
function removeNotification(notifications, _id) {
    return notifications.filter(function (n) { return n._id !== _id; });
}
function updateNotifications(notifications, variables) {
    return variables.archive
        ? removeNotification(notifications, variables._id)
        : variables.read !== undefined
            ? markNotificationAsRead(notifications, variables._id, variables.read)
            : notifications;
}
/**
 * mark a notification as read/unread or archive it
 *
 * will optimistically update the cached values
 */
export function useMutateNotification() {
    var client = useQueryClient();
    return useMutation(function (params) {
        var options = params.options, variables = __rest(params, ["options"]);
        return NotificationsClient.instance.updateNotification(variables, options);
    }, {
        // optimistic update on mutate
        onMutate: function (variables) {
            // cancel any outgoing refetches (so they don't overwrite our optimistic update)
            client.cancelQueries({ queryKey: ['user-notifications'] });
            // get the current unread notificatins count
            var userNotificationsData = client.getQueryData(['user-notifications']);
            client.setQueriesData(['user-notifications-infinite'], function (data) {
                return data
                    ? __assign(__assign({}, data), { pages: data.pages.map(function (p, i) { return (__assign(__assign({}, p), { notifications: updateNotifications(p.notifications, variables), unread: 
                            // decrease the unread count of the first page of notifications if we are marking a notification as read
                            i === 0 && userNotificationsData && variables.read !== undefined
                                ? Math.max(0, userNotificationsData.unread - (variables.read ? 1 : -1))
                                : p.unread })); }) }) : undefined;
            });
        },
    });
}
export function useInfiniteNotifications(params) {
    var _a;
    var client = useQueryClient();
    var q = useInfiniteQuery(['user-notifications-infinite', params], function (_a) {
        var pageParam = _a.pageParam;
        return NotificationsClient.instance.getNotifications(__assign(__assign({}, params), { cursor: pageParam }));
    }, {
        getNextPageParam: function (lastPage, pages) { return lastPage.next_cursor || undefined; },
        refetchOnWindowFocus: false,
        refetchInterval: false,
        cacheTime: 0,
        staleTime: 0,
    });
    var page0 = (_a = q.data) === null || _a === void 0 ? void 0 : _a.pages[0];
    useEffect(function () {
        // when we receive first page, we update the unread count from the user-notifications query
        if (page0 && !(params === null || params === void 0 ? void 0 : params.archived)) {
            client.setQueryData(['user-notifications'], function (data) {
                return data
                    ? __assign(__assign({}, data), { unread: page0.unread }) : undefined;
            });
        }
    }, [client, params === null || params === void 0 ? void 0 : params.archived, page0]);
    return q;
}
